import { useState, useEffect } from "react";
import { connect, useDispatch } from "react-redux";
import remove from "../../../../../../assets/remove.svg";
import Image from "../../../../../../components/Image";
import { hideModal } from "../../../../../../store/actions/modal";
import { Modal, ModalDialog, Title, Text, Actions, Button } from "./styles";

interface ModalConfirmCancelScheduleTaskProps {
  visibleModal?: string | null;
  onConfirm: () => void | Promise<void>;
  taskType: 'opening' | 'closing';
  isMembership: boolean;
  isProcessing?: boolean;
  cancelScheduleTasksStates?: {
    success: boolean;
    loading: boolean;
    error: boolean | string;
  };
}

const ModalConfirmCancelScheduleTask = ({
  visibleModal,
  onConfirm,
  taskType,
  isProcessing = false,
  cancelScheduleTasksStates,
  isMembership
}: ModalConfirmCancelScheduleTaskProps) => {
  const dispatch = useDispatch();
  const [isSuccess, setIsSuccess] = useState(false);

  // Sincronizar isProcessing con el estado de loading del store
  const isActuallyProcessing = isProcessing || cancelScheduleTasksStates?.loading || false;

  const handleConfirm = async () => {
    try {
      await onConfirm();
      // No mostrar éxito inmediatamente, esperar a que el store confirme
    } catch (error) {
      console.error('Error confirming cancellation:', error);
      // Si hay error, no mostrar éxito
    }
  };

  // Esperar a que el store indique que la cancelación fue exitosa
  useEffect(() => {
    if (cancelScheduleTasksStates?.success && !isSuccess) {
      setIsSuccess(true);
      // Cerrar el modal después de 5 segundos
      const timeout = setTimeout(() => {
        setIsSuccess(false);
        dispatch(hideModal());
      }, 5000);

      return () => clearTimeout(timeout);
    }
  }, [cancelScheduleTasksStates?.success, isSuccess, dispatch]);

  // Manejar errores del store
  useEffect(() => {
    if (cancelScheduleTasksStates?.error) {
      console.error('Error cancelling schedule tasks:', cancelScheduleTasksStates.error);
    }
  }, [cancelScheduleTasksStates?.error]);

  const handleClose = () => {
    if (!isActuallyProcessing) {
      setIsSuccess(false);
      dispatch(hideModal());
    }
  };

  // Funciones auxiliares para generar textos de forma más mantenible
  const getGroupTypeText = () => isMembership ? 'membresía' : 'taller';

  const getCurrentStateText = () => {
    if (taskType === 'opening') {
      return isMembership ? 'cerrada' : 'cerrado';
    }
    return isMembership ? 'abierta' : 'abierto';
  };

  const getFinalStateText = () => isMembership ? 'cerrada' : 'cerrado';

  const getModalDescriptionText = () => {
    const groupType = getGroupTypeText();
    const currentState = getCurrentStateText();
    const finalState = getFinalStateText();

    return `Si preferís que tu ${groupType} siga ${currentState}, podés desactivar la apertura y el cierre que tenías definidos. Va a permanecer ${finalState} hasta que vos lo decidas.`;
  };

  const getButtonText = () => {
    if (isActuallyProcessing) {
      return 'Procesando...';
    }
    const currentState = getCurrentStateText();
    return `Eliminar fechas y dejar ${currentState}`;
  };

  const isOpen = visibleModal === 'modal-cancel-schedule-task';

  if (!isOpen) return null;

  return (
    <Modal
      name="modal-cancel-schedule-task"
      width="340"
      onClose={handleClose}
    >
      <ModalDialog>
        {isSuccess ? (
          <>
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}>
              <Image src={remove} width="102px" height="117px" alt={remove} />
            </div>

            <Title>
              Programación cancelada satisfactoriamente
            </Title>
          </>
        ) : (
          <>
            <Title>¿Eliminar las fechas programadas?</Title>

            <Text>
              {getModalDescriptionText()}
            </Text>

            <Actions>
              <Button
                onClick={handleConfirm}
                type="button"
                disabled={isActuallyProcessing}
                options={{
                  skin: "purple",
                  size: "md",
                  type: "filled",
                  block: true,
                }}
              >
                {getButtonText()}
              </Button>
            </Actions>
          </>
        )}
      </ModalDialog>
    </Modal>
  );
};

const state = ({ modalStore, groupStore }) => ({
  ...modalStore,
  cancelScheduleTasksStates: groupStore.cancelScheduleTasks?.states,
});

export default connect(state)(ModalConfirmCancelScheduleTask);

